export type Language = 'en' | 'es'

export const translations = {
  en: {
    nav: {
      services: 'Services',
      about: 'About',
      testimonials: 'Testimonials',
      location: 'Location',
      faq: 'FAQ',
      bookNow: 'Book Now',
      call: 'Call',
    },
    hero: {
      headline:
        'Finally, a Chicago Salon That Understands Your Hair — and Makes You Feel at Home',
      subheadline:
        'A real luxury salon experience — without the Gold Coast price tag or the cold attitude.',
      cta: 'Book Your Appointment',
    },
    lead: {
      p1: 'You know exactly how it feels.',
      p2: 'You walk into a salon with hope. You walk out with hair that looks nothing like what you asked for, a bill higher than you expected… and that same hollow feeling that, once again, nobody really listened to you.',
      p3: "It's not that you haven't tried. You've tested the chain salons where the stylist changes every time and never remembers how you like your cut. You've seen the downtown spots that charge like your hair is a Picasso but treat you like just another body in the chair. And maybe you've even tried doing it yourself at home — with results we won't even talk about.",
      p4: 'The problem was never your hair.',
      p5: "The problem is that you've never found the place where you feel a real connection — with the right stylist, the right products, and the kind of experience that your hair and you have deserved for a long, long time.",
      p6: "That's about to change.",
    },
    letter: {
      headline:
        '"Finally, a Chicago Salon That Understands Your Hair — and Makes You Feel at Home"',
      p1: "There's something most salons will never tell you:",
      p2: 'Not all hair is the same. And not every stylist is ready for yours.',
      p3: 'If you have thick, curly, layered, or hard-to-manage hair… you already know this. You\'ve lived the disappointment. You\'ve paid for "transformations" that lasted three days. You\'ve heard "it looks amazing" while you\'re thinking "this is not what I asked for."',
      p4: "And the worst part isn't the result.",
      p5: 'The worst part is the feeling of never being truly heard.',
    },
    trap: {
      headline: 'THE TRAP WE ALL FALL INTO',
      p1: 'We live in a city with hundreds of salons. And yet, most women we know are still searching. Still waiting to find that place where:',
      items: [
        'The stylist actually knows how to work with their texture',
        'The pricing is fair, honest, and transparent',
        "They don't feel judged or rushed",
        'They can bring their kids, their mom, their partner — and everyone leaves happy',
        'They feel welcomed exactly as they are',
      ],
      p2: 'Why is that so hard to find?',
      p3: 'Because most salons are built to be efficient. Not to create real connection.',
    },
    reason: {
      headline: 'THE REAL REASON NOTHING HAS WORKED',
      p1: "It's not your hair. It's not your budget. It's not bad luck.",
      p2: "It's that you need someone who genuinely gets it.",
      p3: "Hair with history — hair that's seen perms, color, humidity, and years of products — needs hands that truly know it. Hands that know when to push and when to hold back. That use products that nourish instead of destroy.",
      p4: "And beyond that — you need a space where you can breathe. Where the atmosphere is warm. Where you're treated like a person, not a time slot.",
      p5: 'That is exactly what we built at Conexión Salon and Spa.',
    },
    intro: {
      headline: 'INTRODUCING: CONEXIÓN SALON AND SPA',
      p1: "Chicago's full-service beauty salon in the heart of Roscoe Village.",
      p2: "Conexión isn't just a name. It's a promise.",
      p3: 'From the moment you step into our space at 2213 W Roscoe St, something shifts. The atmosphere is warm. The team greets you by name. And Margarita — our founder and lead stylist — makes sure every single client gets exactly what they came for.',
      p4: "We are a Latina-owned, women-led salon built on simple values: real quality, genuine human care, and prices that don't make you choose between beauty and your bills.",
    },
    story: {
      headline: 'THE STORY BEHIND CONEXIÓN',
      tag: 'Founder & Lead Stylist',
      p1: "Margarita didn't build this salon to compete with anyone.",
      p2: 'She built it because she saw a gap in Chicago: Latina women, families of all backgrounds, members of the LGBTQ+ community — all searching for a place where they would truly feel seen and served with excellence.',
      p3: 'Certified in the Paul Mitchell product line — one of the most respected names in professional hair care — and with years of hands-on experience working with every hair type and texture imaginable, Margarita trained her team around one single philosophy:',
      quote:
        '"Every person who walks through that door deserves to leave feeling like the best version of themselves."',
      p4: "That's not marketing copy. That's the reason our clients keep coming back — and keep bringing their friends, their mothers, and their daughters.",
    },
    services: {
      headline: 'WHAT TO EXPECT EVERY TIME YOU VISIT',
      hairTitle: 'HAIR SERVICES',
      wellnessTitle: 'WELLNESS & AESTHETICS',
      hair: [
        {
          s: 'Haircuts (adults & kids)',
          d: 'Precision cutting + technique tailored to your texture',
        },
        {
          s: 'Balayage & Advanced Color',
          d: 'Long-lasting results that look natural and intentional',
        },
        {
          s: 'Perms',
          d: 'Controlled texture, with minimal unnecessary damage',
        },
        {
          s: 'Special Occasion Styling',
          d: 'For events, quinceañeras, weddings, and everything in between',
        },
        {
          s: 'Treatments for curly, thick & fine hair',
          d: 'Real expertise — not guesswork',
        },
      ],
      wellness: [
        { s: 'Waxing', d: 'Smooth skin, fast process, comfortable experience' },
        { s: 'Massages', d: 'Deep relaxation, tension completely released' },
        {
          s: 'Skincare / Facials',
          d: 'Radiant skin using quality professional products',
        },
        {
          s: 'Professional Makeup',
          d: 'For everyday looks or your most important moments',
        },
      ],
    },
    gallery: {
      headline: 'OUR WORK',
      subtitle:
        "Every transformation tells a story. Here are some of the moments we've created with our clients.",
      slides: [
        {
          src: 'https://cdn.magicpatterns.com/uploads/8XEtKzeRXwgahSYKUvB6yk/s1.png',
          label: 'Signature',
          title: 'Layered Cuts & Styling',
          description:
            'Precision layers shaped to your face and texture — movement, volume, and shine that lasts.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/dQUzXXEXUKs2oUQsy6Q6sV/s3.png',
          label: 'Color',
          title: 'Balayage on Curls',
          description:
            'Hand-painted highlights that honor your natural curl pattern and add depth in every light.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/cgh4ex9HW3tSNBU3xbyeqG/s4.png',
          label: 'Volume',
          title: 'Layers & Movement',
          description:
            'Long layers with shape, body, and that effortless swing — color that catches every light.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/cVvAwQHY7zPZZLQZ93X9pg/s2.png',
          label: 'Texture',
          title: 'Curly Pixie',
          description:
            'Short, bold, and full of personality — cuts that celebrate your natural texture.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/9GoWNjcZqepz7oXQkXuXUB/s5.png',
          label: 'Precision',
          title: "Men's Cuts & Fades",
          description:
            'Sharp lines, clean fades, and styles that hold up day after day.',
        },
      ],
    },
    inclusion: {
      headline: 'A SPACE FOR EVERYONE — NO EXCEPTIONS',
      p1: "At Conexión, inclusion isn't a policy written on a sign.",
      p2: "It's part of how we breathe.",
      items: [
        'Wheelchair accessible',
        'Gender-neutral restrooms',
        'Safe and certified space for the LGBTQ+ and transgender community',
        'Service for the whole family — from children to seniors',
      ],
      p3: "Because beauty doesn't have one face. And here, everyone deserves to feel at home.",
    },
    testimonials: {
      headline: 'WHAT PEOPLE WHO KNOW US ARE SAYING',
      google: 'Google Reviews',
      basedOn: 'Based on real client reviews',
      readAll: 'Read all reviews on Google',
      items: [
        {
          name: 'Maria Gonzalez',
          initial: 'M',
          color: 'bg-brand-terracotta',
          time: '2 weeks ago',
          rating: 5,
          q: "I finally found a place that actually understands my curly hair. Margarita is incredible — she took the time to listen, explained everything, and the result was exactly what I wanted. I'm not going anywhere else.",
        },
        {
          name: 'Jessica R.',
          initial: 'J',
          color: 'bg-brand-blue',
          time: '1 month ago',
          rating: 5,
          q: 'The atmosphere is so warm and welcoming. I felt at home from the very first second. The team is professional, the products are top quality, and the price is fair. Highly recommend!',
        },
        {
          name: 'Carla Martinez',
          initial: 'C',
          color: 'bg-brand-brown',
          time: '3 weeks ago',
          rating: 5,
          q: "I bring my whole family. The kids, my mom, myself. We all leave happy — and we don't have to break the bank. Margarita and her team treat everyone like family.",
        },
        {
          name: 'Alex T.',
          initial: 'A',
          color: 'bg-brand-terracotta',
          time: '2 months ago',
          rating: 5,
          q: 'As a member of the LGBTQ+ community, finding a salon where I feel completely respected and seen has been life-changing. Conexión is exactly that place. Beautiful work and even better energy.',
        },
        {
          name: 'Sofia L.',
          initial: 'S',
          color: 'bg-brand-blue',
          time: '1 month ago',
          rating: 5,
          q: 'Got my balayage done here and it came out absolutely stunning. They use Paul Mitchell products and you can really tell the difference in how my hair feels. Worth every penny.',
        },
        {
          name: 'Diana Perez',
          initial: 'D',
          color: 'bg-brand-brown',
          time: '2 weeks ago',
          rating: 5,
          q: "Best salon in Roscoe Village hands down. Spanish-speaking team, super welcoming, and they really know how to work with Latina hair textures. I've been a regular for over a year now.",
        },
      ],
    },
    who: {
      headline: 'WHO IS CONEXIÓN FOR?',
      isFor: 'Conexión is for you if:',
      isNotFor: 'Conexión is NOT for you if:',
      forItems: [
        "You're tired of salons that don't understand your hair type",
        'You want premium salon quality without intimidating prices',
        "You're looking for a place where the whole family is welcome",
        'You value a warm, inclusive, judgment-free environment',
        'You want stylists who actually listen to what you ask for',
      ],
      notForItems: [
        "You're only looking for the cheapest option regardless of quality",
        "You don't value the human experience behind the service",
        'You prefer impersonal chain salons',
      ],
      footer:
        "(And that's perfectly okay. We're not for everyone. Just for those who believe they deserve the best.)",
    },
    promise: {
      headline: 'OUR PROMISE TO YOU',
      p1: 'We enter every single appointment with one goal: for you to leave feeling absolutely incredible.',
      p2: "If something isn't exactly what you asked for, we make it right. No drama, no excuses, no runaround. That simple.",
      p3: "Your satisfaction isn't a slogan here. It's the minimum standard we hold ourselves to every single day.",
    },
    hours: {
      headline: 'OUR HOURS',
      visitUs: 'VISIT US',
      tapToCall: 'Tap to call',
      targetLocation: 'Target Location',
      getDirections: 'Get directions',
      items: [
        { day: 'Monday', time: 'By appointment only' },
        { day: 'Tuesday — Friday', time: '10:30 AM – 7:00 PM' },
        { day: 'Saturday', time: '9:00 AM – 5:00 PM' },
        { day: 'Sunday', time: 'Closed' },
      ],
    },
    cta: {
      headline: 'CALL TO ACTION',
      subheadline: 'Your hair has waited long enough.',
      p1: 'This week, give yourself the experience you deserve.',
      p2: 'Book your appointment today and discover why Chicago women stop searching after their very first visit to Conexión.',
      items: [
        'Book your appointment now — Saturdays fill up fast',
        "Call us today and tell us what you need. We'll handle the rest.",
        "Take the first step toward the hair you've always wanted",
        "Reserve your spot at Conexión — because you've waited long enough",
        'Come visit us this Saturday. No pressure. Just come meet us.',
      ],
    },
    oneliners: {
      headline: 'WHY WE DO THIS',
      items: [
        "It's not just a haircut. It's a moment that's entirely yours.",
        'Your hair has a story. Our hands know how to honor it.',
        "Affordable doesn't mean average. Come see the difference.",
        'The place where your curly hair is finally treated the way it deserves.',
        "Here, you're not a time slot. You're a person.",
        'Walk in as a client. Walk out as the most radiant version of yourself.',
        'Paul Mitchell + expert hands + Latina heart = Conexión.',
        'For the woman who deserves everything — without paying Gold Coast prices.',
        'Your whole family, welcome. Your budget, respected.',
        "Roscoe Village has a secret. It's called Conexión.",
      ],
    },
    faq: {
      headline: 'FAQ — OBJECTION HANDLING',
      items: [
        {
          q: "It's too far from where I live",
          a: "Our clients come from all over Chicago — and they all say it's worth every mile.",
        },
        {
          q: "What if I don't like the result?",
          a: 'We talk with you before, during, and after. Your satisfaction is our responsibility.',
        },
        {
          q: "I'm not sure you can handle my hair type",
          a: "Curly, thick, straight, fine — we're certified for all of them. Just tell us your hair story.",
        },
        {
          q: 'Is it actually safe and welcoming for my family?',
          a: 'Absolutely. We are a fully accessible, inclusive, and family-friendly space. Always.',
        },
        {
          q: 'Do I need an appointment?',
          a: 'We strongly recommend booking ahead to guarantee you get the time and attention you deserve.',
        },
        {
          q: 'Are the products actually good quality?',
          a: 'We work exclusively with the Paul Mitchell professional line — one of the best in the world.',
        },
      ],
    },
    footer: {
      rights: 'Conexión Salon and Spa. All rights reserved.',
    },
    toast: {
      in: 'in',
      justBooked: 'just booked',
      minAgo: 'min ago',
      services: {
        layer: 'Layered Cut & Styling',
        balayage: 'Balayage on Curls',
        movement: 'Layers & Movement',
        pixie: 'Curly Pixie',
        mens: "Men's Cut & Fade",
        special: 'Special Occasion Styling',
        facial: 'Facial Treatment',
        massage: 'Massage Therapy',
      },
      duration: {
        m45: '45 min',
        m60: '60 min',
        m90: '90 min',
        h2m30: '2 hr 30 min',
      },
    },
  },
  es: {
    nav: {
      services: 'Servicios',
      about: 'Nosotros',
      testimonials: 'Testimonios',
      location: 'Ubicación',
      faq: 'Preguntas',
      bookNow: 'Reservar',
      call: 'Llamar',
    },
    hero: {
      headline:
        'Por fin, un salón en Chicago que entiende tu cabello — y te hace sentir en casa',
      subheadline:
        'Una verdadera experiencia de salón de lujo — sin los precios de Gold Coast ni la actitud fría.',
      cta: 'Reserva Tu Cita',
    },
    lead: {
      p1: 'Sabes exactamente cómo se siente.',
      p2: 'Entras a un salón con esperanza. Sales con un cabello que no se parece en nada a lo que pediste, una cuenta más alta de lo esperado... y esa misma sensación vacía de que, una vez más, nadie te escuchó realmente.',
      p3: 'No es que no lo hayas intentado. Has probado los salones de cadena donde el estilista cambia cada vez y nunca recuerda cómo te gusta tu corte. Has visto los lugares del centro que cobran como si tu cabello fuera un Picasso pero te tratan como un cuerpo más en la silla. Y tal vez hasta has intentado hacerlo tú misma en casa — con resultados de los que mejor ni hablamos.',
      p4: 'El problema nunca fue tu cabello.',
      p5: 'El problema es que nunca has encontrado el lugar donde sientas una conexión real — con el estilista adecuado, los productos correctos y el tipo de experiencia que tú y tu cabello han merecido por mucho, mucho tiempo.',
      p6: 'Eso está a punto de cambiar.',
    },
    letter: {
      headline:
        '"Por fin, un salón en Chicago que entiende tu cabello — y te hace sentir en casa"',
      p1: 'Hay algo que la mayoría de los salones nunca te dirán:',
      p2: 'No todo el cabello es igual. Y no cualquier estilista está preparado para el tuyo.',
      p3: 'Si tienes cabello grueso, rizado, en capas o difícil de manejar... ya lo sabes. Has vivido la decepción. Has pagado por "transformaciones" que duraron tres días. Has escuchado "se ve increíble" mientras piensas "esto no es lo que pedí".',
      p4: 'Y la peor parte no es el resultado.',
      p5: 'La peor parte es la sensación de nunca ser verdaderamente escuchada.',
    },
    trap: {
      headline: 'LA TRAMPA EN LA QUE TODAS CAEMOS',
      p1: 'Vivimos en una ciudad con cientos de salones. Y sin embargo, la mayoría de las mujeres que conocemos siguen buscando. Siguen esperando encontrar ese lugar donde:',
      items: [
        'El estilista realmente sepa cómo trabajar con su textura',
        'Los precios sean justos, honestos y transparentes',
        'No se sientan juzgadas ni apresuradas',
        'Puedan llevar a sus hijos, a su mamá, a su pareja — y todos salgan felices',
        'Se sientan bienvenidas exactamente como son',
      ],
      p2: '¿Por qué es tan difícil de encontrar?',
      p3: 'Porque la mayoría de los salones están diseñados para ser eficientes. No para crear una conexión real.',
    },
    reason: {
      headline: 'LA VERDADERA RAZÓN POR LA QUE NADA HA FUNCIONADO',
      p1: 'No es tu cabello. No es tu presupuesto. No es mala suerte.',
      p2: 'Es que necesitas a alguien que genuinamente lo entienda.',
      p3: 'El cabello con historia — cabello que ha pasado por permanentes, color, humedad y años de productos — necesita manos que realmente lo conozcan. Manos que sepan cuándo presionar y cuándo detenerse. Que usen productos que nutran en lugar de destruir.',
      p4: 'Y más allá de eso — necesitas un espacio donde puedas respirar. Donde el ambiente sea cálido. Donde te traten como a una persona, no como a un espacio en la agenda.',
      p5: 'Eso es exactamente lo que construimos en Conexión Salon and Spa.',
    },
    intro: {
      headline: 'PRESENTANDO: CONEXIÓN SALON AND SPA',
      p1: 'El salón de belleza de servicio completo de Chicago en el corazón de Roscoe Village.',
      p2: 'Conexión no es solo un nombre. Es una promesa.',
      p3: 'Desde el momento en que entras a nuestro espacio en 2213 W Roscoe St, algo cambia. El ambiente es cálido. El equipo te saluda por tu nombre. Y Margarita — nuestra fundadora y estilista principal — se asegura de que cada cliente obtenga exactamente lo que vino a buscar.',
      p4: 'Somos un salón propiedad de latinas, liderado por mujeres, construido sobre valores simples: calidad real, cuidado humano genuino y precios que no te hacen elegir entre la belleza y tus cuentas.',
    },
    story: {
      headline: 'LA HISTORIA DETRÁS DE CONEXIÓN',
      tag: 'Fundadora y Estilista Principal',
      p1: 'Margarita no construyó este salón para competir con nadie.',
      p2: 'Lo construyó porque vio un vacío en Chicago: mujeres latinas, familias de todos los orígenes, miembros de la comunidad LGBTQ+ — todos buscando un lugar donde realmente se sintieran vistos y atendidos con excelencia.',
      p3: 'Certificada en la línea de productos Paul Mitchell — uno de los nombres más respetados en el cuidado profesional del cabello — y con años de experiencia práctica trabajando con cada tipo y textura de cabello imaginable, Margarita entrenó a su equipo en torno a una sola filosofía:',
      quote:
        '"Cada persona que cruza esa puerta merece salir sintiéndose como la mejor versión de sí misma."',
      p4: 'Eso no es texto de marketing. Esa es la razón por la que nuestros clientes siguen regresando — y siguen trayendo a sus amigas, a sus madres y a sus hijas.',
    },
    services: {
      headline: 'LO QUE PUEDES ESPERAR EN CADA VISITA',
      hairTitle: 'SERVICIOS DE CABELLO',
      wellnessTitle: 'BIENESTAR Y ESTÉTICA',
      hair: [
        {
          s: 'Cortes (adultos y niños)',
          d: 'Corte de precisión + técnica adaptada a tu textura',
        },
        {
          s: 'Balayage y Color Avanzado',
          d: 'Resultados duraderos que se ven naturales e intencionales',
        },
        {
          s: 'Permanentes',
          d: 'Textura controlada, con el mínimo daño innecesario',
        },
        {
          s: 'Peinados para Ocasiones Especiales',
          d: 'Para eventos, quinceañeras, bodas y todo lo demás',
        },
        {
          s: 'Tratamientos para cabello rizado, grueso y fino',
          d: 'Experiencia real — sin adivinanzas',
        },
      ],
      wellness: [
        {
          s: 'Depilación con Cera',
          d: 'Piel suave, proceso rápido, experiencia cómoda',
        },
        {
          s: 'Masajes',
          d: 'Relajación profunda, tensión completamente liberada',
        },
        {
          s: 'Cuidado de la Piel / Faciales',
          d: 'Piel radiante usando productos profesionales de calidad',
        },
        {
          s: 'Maquillaje Profesional',
          d: 'Para looks diarios o tus momentos más importantes',
        },
      ],
    },
    gallery: {
      headline: 'NUESTRO TRABAJO',
      subtitle:
        'Cada transformación cuenta una historia. Aquí hay algunos de los momentos que hemos creado con nuestros clientes.',
      slides: [
        {
          src: 'https://cdn.magicpatterns.com/uploads/8XEtKzeRXwgahSYKUvB6yk/s1.png',
          label: 'Firma',
          title: 'Cortes en Capas y Estilo',
          description:
            'Capas de precisión moldeadas a tu rostro y textura — movimiento, volumen y brillo que perdura.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/dQUzXXEXUKs2oUQsy6Q6sV/s3.png',
          label: 'Color',
          title: 'Balayage en Rizos',
          description:
            'Luces pintadas a mano que honran tu patrón natural de rizos y añaden profundidad en cada luz.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/cgh4ex9HW3tSNBU3xbyeqG/s4.png',
          label: 'Volumen',
          title: 'Capas y Movimiento',
          description:
            'Capas largas con forma, cuerpo y ese balanceo natural — un color que atrapa cada luz.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/cVvAwQHY7zPZZLQZ93X9pg/s2.png',
          label: 'Textura',
          title: 'Pixie Rizado',
          description:
            'Corto, audaz y lleno de personalidad — cortes que celebran tu textura natural.',
        },
        {
          src: 'https://cdn.magicpatterns.com/uploads/9GoWNjcZqepz7oXQkXuXUB/s5.png',
          label: 'Precisión',
          title: 'Cortes y Fades para Hombre',
          description:
            'Líneas nítidas, fades limpios y estilos que se mantienen día tras día.',
        },
      ],
    },
    inclusion: {
      headline: 'UN ESPACIO PARA TODOS — SIN EXCEPCIONES',
      p1: 'En Conexión, la inclusión no es una política escrita en un letrero.',
      p2: 'Es parte de cómo respiramos.',
      items: [
        'Accesible para sillas de ruedas',
        'Baños de género neutro',
        'Espacio seguro y certificado para la comunidad LGBTQ+ y transgénero',
        'Servicio para toda la familia — desde niños hasta adultos mayores',
      ],
      p3: 'Porque la belleza no tiene una sola cara. Y aquí, todos merecen sentirse en casa.',
    },
    testimonials: {
      headline: 'LO QUE DICEN LAS PERSONAS QUE NOS CONOCEN',
      google: 'Reseñas de Google',
      basedOn: 'Basado en reseñas reales de clientes',
      readAll: 'Leer todas las reseñas en Google',
      items: [
        {
          name: 'Maria Gonzalez',
          initial: 'M',
          color: 'bg-brand-terracotta',
          time: 'Hace 2 semanas',
          rating: 5,
          q: 'Por fin encontré un lugar que realmente entiende mi cabello rizado. Margarita es increíble — se tomó el tiempo para escuchar, explicó todo, y el resultado fue exactamente lo que quería. No iré a ningún otro lado.',
        },
        {
          name: 'Jessica R.',
          initial: 'J',
          color: 'bg-brand-blue',
          time: 'Hace 1 mes',
          rating: 5,
          q: 'El ambiente es tan cálido y acogedor. Me sentí en casa desde el primer segundo. El equipo es profesional, los productos son de primera calidad y el precio es justo. ¡Súper recomendado!',
        },
        {
          name: 'Carla Martinez',
          initial: 'C',
          color: 'bg-brand-brown',
          time: 'Hace 3 semanas',
          rating: 5,
          q: 'Traigo a toda mi familia. A los niños, a mi mamá, a mí misma. Todos salimos felices — y no tenemos que gastar una fortuna. Margarita y su equipo tratan a todos como familia.',
        },
        {
          name: 'Alex T.',
          initial: 'A',
          color: 'bg-brand-terracotta',
          time: 'Hace 2 meses',
          rating: 5,
          q: 'Como miembro de la comunidad LGBTQ+, encontrar un salón donde me siento completamente respetado y visto ha cambiado mi vida. Conexión es exactamente ese lugar. Un trabajo hermoso y una energía aún mejor.',
        },
        {
          name: 'Sofia L.',
          initial: 'S',
          color: 'bg-brand-blue',
          time: 'Hace 1 mes',
          rating: 5,
          q: 'Me hice el balayage aquí y quedó absolutamente impresionante. Usan productos Paul Mitchell y realmente se nota la diferencia en cómo se siente mi cabello. Vale cada centavo.',
        },
        {
          name: 'Diana Perez',
          initial: 'D',
          color: 'bg-brand-brown',
          time: 'Hace 2 semanas',
          rating: 5,
          q: 'El mejor salón en Roscoe Village sin duda. Equipo que habla español, súper acogedor, y realmente saben cómo trabajar con las texturas de cabello latino. He sido clienta habitual por más de un año.',
        },
      ],
    },
    who: {
      headline: '¿PARA QUIÉN ES CONEXIÓN?',
      isFor: 'Conexión es para ti si:',
      isNotFor: 'Conexión NO es para ti si:',
      forItems: [
        'Estás cansada de salones que no entienden tu tipo de cabello',
        'Quieres calidad de salón premium sin precios intimidantes',
        'Buscas un lugar donde toda la familia sea bienvenida',
        'Valoras un ambiente cálido, inclusivo y libre de juicios',
        'Quieres estilistas que realmente escuchen lo que pides',
      ],
      notForItems: [
        'Solo buscas la opción más barata sin importar la calidad',
        'No valoras la experiencia humana detrás del servicio',
        'Prefieres los salones de cadena impersonales',
      ],
      footer:
        '(Y eso está perfectamente bien. No somos para todos. Solo para aquellos que creen que merecen lo mejor.)',
    },
    promise: {
      headline: 'NUESTRA PROMESA PARA TI',
      p1: 'Entramos a cada cita con un solo objetivo: que salgas sintiéndote absolutamente increíble.',
      p2: 'Si algo no es exactamente lo que pediste, lo arreglamos. Sin dramas, sin excusas, sin rodeos. Así de simple.',
      p3: 'Tu satisfacción no es un eslogan aquí. Es el estándar mínimo que nos exigimos todos los días.',
    },
    hours: {
      headline: 'NUESTROS HORARIOS',
      visitUs: 'VISÍTANOS',
      tapToCall: 'Toca para llamar',
      targetLocation: 'Ubicación',
      getDirections: 'Cómo llegar',
      items: [
        { day: 'Lunes', time: 'Solo con cita previa' },
        { day: 'Martes — Viernes', time: '10:30 AM – 7:00 PM' },
        { day: 'Sábado', time: '9:00 AM – 5:00 PM' },
        { day: 'Domingo', time: 'Cerrado' },
      ],
    },
    cta: {
      headline: 'LLAMADO A LA ACCIÓN',
      subheadline: 'Tu cabello ha esperado lo suficiente.',
      p1: 'Esta semana, date la experiencia que mereces.',
      p2: 'Reserva tu cita hoy y descubre por qué las mujeres de Chicago dejan de buscar después de su primera visita a Conexión.',
      items: [
        'Reserva tu cita ahora — los sábados se llenan rápido',
        'Llámanos hoy y dinos qué necesitas. Nosotros nos encargamos del resto.',
        'Da el primer paso hacia el cabello que siempre has querido',
        'Reserva tu lugar en Conexión — porque has esperado lo suficiente',
        'Ven a visitarnos este sábado. Sin presión. Solo ven a conocernos.',
      ],
    },
    oneliners: {
      headline: 'POR QUÉ HACEMOS ESTO',
      items: [
        'No es solo un corte de cabello. Es un momento que es completamente tuyo.',
        'Tu cabello tiene una historia. Nuestras manos saben cómo honrarla.',
        'Accesible no significa promedio. Ven a ver la diferencia.',
        'El lugar donde tu cabello rizado por fin es tratado como merece.',
        'Aquí, no eres un espacio en la agenda. Eres una persona.',
        'Entra como cliente. Sal como la versión más radiante de ti misma.',
        'Paul Mitchell + manos expertas + corazón latino = Conexión.',
        'Para la mujer que lo merece todo — sin pagar precios de Gold Coast.',
        'Toda tu familia, bienvenida. Tu presupuesto, respetado.',
        'Roscoe Village tiene un secreto. Se llama Conexión.',
      ],
    },
    faq: {
      headline: 'PREGUNTAS FRECUENTES',
      items: [
        {
          q: 'Está muy lejos de donde vivo',
          a: 'Nuestros clientes vienen de todo Chicago — y todos dicen que vale la pena cada milla.',
        },
        {
          q: '¿Qué pasa si no me gusta el resultado?',
          a: 'Hablamos contigo antes, durante y después. Tu satisfacción es nuestra responsabilidad.',
        },
        {
          q: 'No estoy segura de que puedan manejar mi tipo de cabello',
          a: 'Rizado, grueso, lacio, fino — estamos certificados para todos. Solo cuéntanos la historia de tu cabello.',
        },
        {
          q: '¿Es realmente seguro y acogedor para mi familia?',
          a: 'Absolutamente. Somos un espacio totalmente accesible, inclusivo y familiar. Siempre.',
        },
        {
          q: '¿Necesito una cita?',
          a: 'Recomendamos encarecidamente reservar con anticipación para garantizar que obtengas el tiempo y la atención que mereces.',
        },
        {
          q: '¿Los productos son realmente de buena calidad?',
          a: 'Trabajamos exclusivamente con la línea profesional de Paul Mitchell — una de las mejores del mundo.',
        },
      ],
    },
    footer: {
      rights: 'Conexión Salon and Spa. Todos los derechos reservados.',
    },
    toast: {
      in: 'en',
      justBooked: 'acaba de reservar',
      minAgo: 'hace min',
      services: {
        layer: 'Corte en Capas y Estilo',
        balayage: 'Balayage en Rizos',
        movement: 'Capas y Movimiento',
        pixie: 'Pixie Rizado',
        mens: 'Corte y Fade para Hombre',
        special: 'Peinado para Ocasión Especial',
        facial: 'Tratamiento Facial',
        massage: 'Terapia de Masaje',
      },
      duration: {
        m45: '45 min',
        m60: '60 min',
        m90: '90 min',
        h2m30: '2 hr 30 min',
      },
    },
  },
}
